import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, FileText, CreditCard, Shield, AlertTriangle } from "lucide-react";
import { Button } from "../components/ui/button";

export default function TermsOfServicePage() {
  const lastUpdated = "June 9, 2026";

  return (
    <div className="min-h-screen bg-[#0A0A0B] text-white">
      {/* Header */}
      <header className="border-b border-border/50 bg-[#0A0A0B]/95 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#00F0FF]" />
            <span className="font-semibold">Terms of Service</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <motion.main
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-4xl mx-auto px-4 py-12"
      >
        <div className="prose prose-invert prose-lg max-w-none">
          <h1 className="text-3xl font-bold text-white mb-2">Terms of Service</h1>
          <p className="text-muted-foreground mb-8">Last updated: {lastUpdated}</p>

          {/* Quick Navigation */}
          <div className="bg-secondary/30 rounded-lg p-4 mb-8 not-prose">
            <p className="text-sm text-gray-400 mb-3">Quick Navigation:</p>
            <div className="flex flex-wrap gap-2">
              <a href="#payments" className="text-xs px-3 py-1.5 bg-[#00F0FF]/10 text-[#00F0FF] rounded-full hover:bg-[#00F0FF]/20 transition-colors flex items-center gap-1">
                <CreditCard className="w-3 h-3" /> Payments & Refunds
              </a>
              <a href="#content" className="text-xs px-3 py-1.5 bg-[#00F0FF]/10 text-[#00F0FF] rounded-full hover:bg-[#00F0FF]/20 transition-colors flex items-center gap-1">
                <Shield className="w-3 h-3" /> Content Rights
              </a>
              <a href="#ai-content" className="text-xs px-3 py-1.5 bg-[#00F0FF]/10 text-[#00F0FF] rounded-full hover:bg-[#00F0FF]/20 transition-colors flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> AI Content
              </a>
            </div>
          </div>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-300 mb-4">
              By accessing or using iGoViral Content Creator ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use the Service.
            </p>
            <p className="text-gray-300">
              We reserve the right to modify these Terms at any time. We will notify you of material changes via email or in-app notification. Your continued use of the Service after any changes constitutes acceptance of the new Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">2. Description of Service</h2>
            <p className="text-gray-300 mb-4">
              iGoViral Content Creator is an AI-powered platform that helps users create video content for social media platforms. Our Service includes:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>AI-generated video scripts and content</li>
              <li>Video creation and editing tools</li>
              <li>AI-powered voiceover generation</li>
              <li>Trend analysis and content recommendations</li>
              <li>Export tools for social media platforms</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">3. User Accounts</h2>
            <p className="text-gray-300 mb-4">
              To use certain features of the Service, you must create an account. You agree to:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>Provide accurate and complete registration information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Notify us immediately of any unauthorized access</li>
              <li>Accept responsibility for all activities under your account</li>
            </ul>
            <p className="text-gray-300 mt-4">
              You must be at least 13 years old to use this Service. If you are under 18, you must have parental consent.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">4. User Content</h2>
            <p className="text-gray-300 mb-4">
              You retain ownership of any content you create using our Service ("User Content"). By using the Service, you grant us a limited license to process, store, and display your User Content as necessary to provide the Service.
            </p>
            <p className="text-gray-300 mb-4">You agree not to create or upload content that:</p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>Violates any applicable laws or regulations</li>
              <li>Infringes on intellectual property rights of others</li>
              <li>Contains harmful, threatening, or harassing material</li>
              <li>Includes sexually explicit or pornographic content</li>
              <li>Promotes violence, discrimination, or illegal activities</li>
              <li>Contains malware or malicious code</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">5. AI-Generated Content</h2>
            <p className="text-gray-300 mb-4">
              Our Service uses artificial intelligence to generate content. You acknowledge that:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>AI-generated content may not always be accurate or appropriate</li>
              <li>You are responsible for reviewing and editing AI-generated content before use</li>
              <li>AI outputs may occasionally produce unexpected or unintended results</li>
              <li>You should not rely solely on AI-generated content for important decisions</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">6. Third-Party Services</h2>
            <p className="text-gray-300 mb-4">
              Our Service integrates with third-party services including but not limited to:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>AI providers (OpenAI, Google Gemini, fal.ai) for content generation</li>
              <li>Voice synthesis services (ElevenLabs, Google TTS)</li>
              <li>Authentication providers (Google OAuth)</li>
              <li>Payment processors (Stripe)</li>
              <li>Social media platforms (TikTok, Instagram)</li>
            </ul>
            <p className="text-gray-300 mt-4">
              Your use of these third-party services is subject to their respective terms and privacy policies.
            </p>
          </section>

          <section className="mb-8" id="payments">
            <h2 className="text-xl font-semibold text-white mb-4">7. Subscription Plans & Payments</h2>
            
            <h3 className="text-lg font-medium text-white mb-3 mt-6">7.1 Subscription Terms</h3>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>Subscriptions are billed monthly or annually, depending on your chosen plan</li>
              <li>Payment is processed securely through Stripe</li>
              <li>Your subscription automatically renews unless canceled before the renewal date</li>
              <li>Prices may change with 30 days' notice to existing subscribers</li>
            </ul>

            <h3 className="text-lg font-medium text-white mb-3 mt-6">7.2 Free Trial</h3>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>New users may be eligible for a free trial period</li>
              <li>Trial limits and duration are specified at signup</li>
              <li>Payment method may be required to start a trial</li>
              <li>You will be charged when the trial ends unless you cancel</li>
            </ul>

            <h3 className="text-lg font-medium text-white mb-3 mt-6">7.3 Refund Policy</h3>
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mt-4">
              <p className="text-amber-200 font-medium mb-2">Important: AI Generation Costs</p>
              <p className="text-gray-300 text-sm">
                Due to the nature of AI video generation, which incurs real costs per video created, 
                refunds are evaluated on a case-by-case basis.
              </p>
            </div>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4 mt-4">
              <li><strong>Within 24 hours:</strong> Full refund if no videos have been generated</li>
              <li><strong>Technical issues:</strong> Prorated refund or credits for documented service failures</li>
              <li><strong>After video generation:</strong> Refunds generally not available as AI costs have been incurred</li>
              <li><strong>Annual plans:</strong> Prorated refund available within first 14 days minus any AI usage costs</li>
            </ul>
            <p className="text-gray-300 mt-4">
              To request a refund, contact us at <span className="text-[#00F0FF]">billing@igoviralcreator.io</span> with your account details and reason for the request.
            </p>

            <h3 className="text-lg font-medium text-white mb-3 mt-6">7.4 Cancellation</h3>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>You may cancel your subscription at any time from your account settings</li>
              <li>Cancellation takes effect at the end of your current billing period</li>
              <li>You retain access to paid features until the end of your billing period</li>
              <li>No partial refunds for unused time in the current billing period</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">8. Intellectual Property</h2>
            <p className="text-gray-300 mb-4">
              The Service, including its original content, features, and functionality, is owned by iGoViral and is protected by international copyright, trademark, and other intellectual property laws.
            </p>
            <p className="text-gray-300">
              You may not copy, modify, distribute, sell, or lease any part of our Service without explicit permission.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">9. Fair Use Policy</h2>
            <p className="text-gray-300 mb-4">
              To ensure fair access for all users, we implement reasonable usage limits:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>Video generation limits vary by subscription tier</li>
              <li>API rate limits apply to prevent abuse</li>
              <li>Excessive automated usage may result in account restrictions</li>
              <li>Sharing account credentials is prohibited</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">10. Limitation of Liability</h2>
            <p className="text-gray-300 mb-4">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, IGOVIRAL SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>Loss of profits, data, or business opportunities</li>
              <li>Service interruptions or failures</li>
              <li>Content generated by AI that is inaccurate or inappropriate</li>
              <li>Actions taken by third-party platforms regarding your content</li>
              <li>Failed video generations due to AI provider issues</li>
            </ul>
            <p className="text-gray-300 mt-4">
              Our total liability shall not exceed the amount you paid for the Service in the 12 months preceding the claim.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">11. Disclaimer of Warranties</h2>
            <p className="text-gray-300">
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. WE DO NOT GUARANTEE THAT THE SERVICE WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE. AI-GENERATED CONTENT MAY CONTAIN ERRORS OR INACCURACIES.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">12. Termination</h2>
            <p className="text-gray-300 mb-4">
              We may terminate or suspend your account and access to the Service immediately, without prior notice, for any reason, including:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>Violation of these Terms</li>
              <li>Fraudulent or illegal activity</li>
              <li>Abuse of the Service or other users</li>
              <li>Non-payment of subscription fees</li>
            </ul>
            <p className="text-gray-300 mt-4">
              Upon termination, your right to use the Service will cease immediately. You may request a copy of your data within 30 days of termination.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">13. Dispute Resolution</h2>
            <p className="text-gray-300 mb-4">
              Any disputes arising from these Terms or your use of the Service shall be:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>First attempted to be resolved through good-faith negotiation</li>
              <li>If unresolved, submitted to binding arbitration</li>
              <li>Class action lawsuits are waived to the extent permitted by law</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">14. Governing Law</h2>
            <p className="text-gray-300">
              These Terms shall be governed by and construed in accordance with applicable laws, without regard to conflict of law principles. You agree to submit to the exclusive jurisdiction of the courts in the applicable jurisdiction.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">15. Contact Us</h2>
            <p className="text-gray-300">
              If you have any questions about these Terms, please contact us at:
            </p>
            <div className="mt-4 space-y-2">
              <p className="text-gray-300"><strong>General Support:</strong> <span className="text-[#00F0FF]">support@igoviralcreator.io</span></p>
              <p className="text-gray-300"><strong>Billing Issues:</strong> <span className="text-[#00F0FF]">billing@igoviralcreator.io</span></p>
              <p className="text-gray-300"><strong>Legal Inquiries:</strong> <span className="text-[#00F0FF]">legal@igoviralcreator.io</span></p>
              <p className="text-gray-300 mt-2">Website: <a href="https://igoviralcreator.io" className="text-[#00F0FF] hover:underline">igoviralcreator.io</a></p>
            </div>
          </section>
        </div>

        {/* Back to Home */}
        <div className="mt-12 pt-8 border-t border-border/50 flex flex-col items-center gap-4">
          <Link to="/">
            <Button variant="outline" className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Button>
          </Link>
          <p className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} iGoViral Content Creator. All rights reserved.
          </p>
        </div>
      </motion.main>
    </div>
  );
}
